/**
 * Extraction Service
 * Handles element extraction operations with Forge Viewer
 * 
 * This service provides comprehensive element data extraction capabilities:
 * - Extract all unique categories from the model
 * - Extract elements by category with progress tracking
 * - Parse family names from element names (handles bracketed IDs)
 * - Group elements by families with count statistics
 * - Filter already extracted elements by family
 * - Export extracted data to JSON format
 * 
 * The service uses bulk property APIs for efficient data retrieval and
 * provides progress callbacks for long-running operations. Family parsing
 * handles Revit naming conventions where element names include bracketed IDs.
 */

import type { ExtractedElement } from '@/store/extractionStore';

export interface FamilyInfo {
  name: string;
  count: number;
  dbIds: number[];
}

export class ExtractionService {
  /**
   * Parse family name from element name
   * Extracts the portion before the bracketed ID
   */
  static parseFamilyName(elementName: string): string {
    // Check if there's a bracketed ID at the end
    const bracketMatch = elementName.match(/^(.+?)\s*\[\d+\]$/);
    
    if (bracketMatch) {
      // Return the part before the brackets, trimmed
      return bracketMatch[1].trim();
    }
    
    // If no brackets, return the entire name trimmed
    return elementName.trim();
  }
  /**
   * Get all unique categories from the model
   */
  static async getCategories(viewer: Autodesk.Viewing.GuiViewer3D): Promise<string[]> {
    if (!viewer || !viewer.model) {
      throw new Error('Viewer or model not available');
    }

    const tree = viewer.model.getInstanceTree();
    if (!tree) {
      throw new Error('Model tree not available');
    }

    // Get all element IDs
    const allDbIds: number[] = [];
    tree.enumNodeChildren(tree.getRootId(), (dbId) => {
      allDbIds.push(dbId);
    }, true);

    console.log(`Found ${allDbIds.length} total elements in model`);

    // Get categories using bulk properties
    const categories = new Set<string>();
    const batchSize = 100;

    for (let i = 0; i < allDbIds.length; i += batchSize) {
      const batch = allDbIds.slice(i, i + batchSize);

      await new Promise<void>((resolve) => {
        viewer.model.getBulkProperties(batch, ['Category'], (results) => {
          results.forEach(result => {
            if (result.properties && result.properties.length > 0) {
              const categoryProp = result.properties[0];
              if (categoryProp && categoryProp.displayValue) {
                categories.add(categoryProp.displayValue);
              }
            }
          });
          resolve();
        }, (error) => {
          console.error('Error in bulk properties:', error);
          resolve();
        });
      });
    }

    return Array.from(categories).sort();
  }

  /**
   * Extract elements by category with progress tracking
   */
  static async extractByCategory(
    viewer: Autodesk.Viewing.GuiViewer3D, 
    category: string,
    onProgress?: (progress: number) => void
  ): Promise<ExtractedElement[]> {
    if (!viewer || !viewer.model) {
      throw new Error('Viewer or model not available');
    }

    const tree = viewer.model.getInstanceTree();
    if (!tree) {
      throw new Error('Model tree not available');
    }

    // Get all element IDs
    const allDbIds: number[] = [];
    tree.enumNodeChildren(tree.getRootId(), (dbId) => {
      allDbIds.push(dbId);
    }, true);

    const extractedElements: ExtractedElement[] = [];
    const batchSize = 50;
    const totalBatches = Math.ceil(allDbIds.length / batchSize);
    let processedBatches = 0;

    // Process in batches with progress
    for (let i = 0; i < allDbIds.length; i += batchSize) {
      const batch = allDbIds.slice(i, i + batchSize);

      await new Promise<void>((resolve) => {
        viewer.model.getBulkProperties(batch, 
          ['Category', 'Name', 'Family', 'Type', 'Level', 'Element ID'],
          (results) => {
            results.forEach(result => {
              const categoryProp = result.properties.find(p => p.displayName === 'Category');
              
              if (categoryProp && categoryProp.displayValue === category) {
                const element: ExtractedElement = {
                  dbId: result.dbId,
                  name: '',
                  category: category,
                  properties: {}
                };

                // Extract all properties
                result.properties.forEach(prop => {
                  const value = prop.displayValue;
                  
                  switch(prop.displayName) {
                    case 'Name':
                      element.name = value || 'Unnamed';
                      break;
                    case 'Family':
                      element.family = value;
                      break;
                    case 'Type':
                      element.type = value;
                      break;
                    case 'Level':
                      element.level = value;
                      break;
                    default:
                      element.properties[prop.displayName] = value;
                  }
                });

                // Use tree node name if element name is empty
                if (!element.name || element.name === 'Unnamed') {
                  element.name = tree.getNodeName(result.dbId) || `Element ${result.dbId}`;
                }

                extractedElements.push(element);
              }
            });

            processedBatches++;
            if (onProgress) {
              onProgress((processedBatches / totalBatches) * 100);
            }
            resolve();
          },
          (error) => {
            console.error('Error in bulk properties:', error);
            resolve();
          }
        );
      });
    }

    return extractedElements;
  }

  /**
   * Get families from extracted elements by parsing their names
   */
  static getFamiliesFromElements(elements: ExtractedElement[]): FamilyInfo[] {
    const familyMap = new Map<string, number[]>();
    
    elements.forEach(element => {
      // Parse family name from element name
      const familyName = this.parseFamilyName(element.name);
      
      if (!familyMap.has(familyName)) {
        familyMap.set(familyName, []);
      }
      familyMap.get(familyName)!.push(element.dbId);
    });
    
    // Convert map to FamilyInfo array
    const families: FamilyInfo[] = Array.from(familyMap.entries())
      .map(([name, dbIds]) => ({
        name,
        count: dbIds.length,
        dbIds
      }))
      .sort((a, b) => {
        // Sort alphabetically by family name
        return a.name.localeCompare(b.name);
      });
    
    return families;
  }

  /**
   * Get all families with element counts for a specific category (OLD - using Family property)
   */
  static async getFamiliesByCategory(
    viewer: Autodesk.Viewing.GuiViewer3D,
    category: string
  ): Promise<FamilyInfo[]> {
    if (!viewer || !viewer.model) {
      throw new Error('Viewer or model not available');
    }

    const tree = viewer.model.getInstanceTree();
    if (!tree) {
      throw new Error('Model tree not available');
    }

    // Get all element IDs
    const allDbIds: number[] = [];
    tree.enumNodeChildren(tree.getRootId(), (dbId) => {
      allDbIds.push(dbId);
    }, true);

    // Map to store family information
    const familyMap = new Map<string, number[]>();
    const batchSize = 100;

    for (let i = 0; i < allDbIds.length; i += batchSize) {
      const batch = allDbIds.slice(i, i + batchSize);

      await new Promise<void>((resolve) => {
        viewer.model.getBulkProperties(batch, ['Category', 'Family', 'Type'], (results) => {
          results.forEach(result => {
            const categoryProp = result.properties.find(p => p.displayName === 'Category');
            const familyProp = result.properties.find(p => p.displayName === 'Family');
            
            if (categoryProp && categoryProp.displayValue === category) {
              const familyName = familyProp?.displayValue || 'No Family';
              
              if (!familyMap.has(familyName)) {
                familyMap.set(familyName, []);
              }
              familyMap.get(familyName)!.push(result.dbId);
            }
          });
          resolve();
        }, (error) => {
          console.error('Error getting families:', error);
          resolve();
        });
      });
    }

    // Convert map to FamilyInfo array
    const families: FamilyInfo[] = Array.from(familyMap.entries())
      .map(([name, dbIds]) => ({
        name,
        count: dbIds.length,
        dbIds
      }))
      .sort((a, b) => b.count - a.count); // Sort by count descending

    return families;
  }

  /**
   * Filter already extracted elements by family name
   */
  static filterElementsByFamily(
    elements: ExtractedElement[],
    familyName: string
  ): ExtractedElement[] {
    return elements.filter(element => {
      const elementFamilyName = this.parseFamilyName(element.name);
      return elementFamilyName === familyName;
    });
  }

  /**
   * Extract elements by family (OLD - using Family property)
   */
  static async extractByFamily(
    viewer: Autodesk.Viewing.GuiViewer3D, 
    category: string,
    familyName: string,
    onProgress?: (progress: number) => void
  ): Promise<ExtractedElement[]> {
    if (!viewer || !viewer.model) {
      throw new Error('Viewer or model not available');
    }

    const tree = viewer.model.getInstanceTree();
    if (!tree) {
      throw new Error('Model tree not available');
    }

    // Get all element IDs
    const allDbIds: number[] = [];
    tree.enumNodeChildren(tree.getRootId(), (dbId) => {
      allDbIds.push(dbId);
    }, true);

    const extractedElements: ExtractedElement[] = [];
    const batchSize = 50;
    const totalBatches = Math.ceil(allDbIds.length / batchSize);
    let processedBatches = 0;

    // Process in batches with progress
    for (let i = 0; i < allDbIds.length; i += batchSize) {
      const batch = allDbIds.slice(i, i + batchSize);

      await new Promise<void>((resolve) => {
        viewer.model.getBulkProperties(batch, 
          ['Category', 'Name', 'Family', 'Type', 'Level', 'Element ID'],
          (results) => {
            results.forEach(result => {
              const categoryProp = result.properties.find(p => p.displayName === 'Category');
              const familyProp = result.properties.find(p => p.displayName === 'Family');
              
              if (categoryProp && categoryProp.displayValue === category &&
                  familyProp && familyProp.displayValue === familyName) {
                const element: ExtractedElement = {
                  dbId: result.dbId,
                  name: '',
                  category: category,
                  family: familyName,
                  properties: {}
                };

                // Extract all properties
                result.properties.forEach(prop => {
                  const value = prop.displayValue;
                  
                  switch(prop.displayName) {
                    case 'Name':
                      element.name = value || 'Unnamed';
                      break;
                    case 'Type':
                      element.type = value;
                      break;
                    case 'Level':
                      element.level = value;
                      break;
                    default:
                      element.properties[prop.displayName] = value;
                  }
                });

                // Use tree node name if element name is empty
                if (!element.name || element.name === 'Unnamed') {
                  element.name = tree.getNodeName(result.dbId) || `Element ${result.dbId}`;
                }

                extractedElements.push(element);
              }
            });

            processedBatches++;
            if (onProgress) {
              onProgress((processedBatches / totalBatches) * 100);
            }
            resolve();
          },
          (error) => {
            console.error('Error in bulk properties:', error);
            resolve();
          }
        );
      });
    }

    return extractedElements;
  }

  /**
   * Extract elements by multiple categories
   * Consolidates duplicate functionality from various validators
   */
  static async extractByMultipleCategories(
    viewer: Autodesk.Viewing.GuiViewer3D,
    categories: string[],
    onProgress?: (progress: number) => void
  ): Promise<ExtractedElement[]> {
    if (!viewer || !viewer.model) {
      throw new Error('Viewer or model not available');
    }

    const tree = viewer.model.getInstanceTree();
    if (!tree) {
      throw new Error('Model tree not available');
    }

    // Get all element IDs
    const allDbIds: number[] = [];
    tree.enumNodeChildren(tree.getRootId(), (dbId) => {
      allDbIds.push(dbId);
    }, true);

    const extractedElements: ExtractedElement[] = [];
    const batchSize = 50;
    const totalBatches = Math.ceil(allDbIds.length / batchSize);
    let processedBatches = 0;
    const categorySet = new Set(categories);

    // Process in batches with progress
    for (let i = 0; i < allDbIds.length; i += batchSize) {
      const batch = allDbIds.slice(i, i + batchSize);

      await new Promise<void>((resolve) => {
        viewer.model.getBulkProperties(batch, 
          ['Category', 'Name', 'Family', 'Type', 'Level', 'Element ID'],
          (results) => {
            results.forEach(result => {
              const categoryProp = result.properties.find(p => p.displayName === 'Category');
              
              if (categoryProp && categorySet.has(categoryProp.displayValue)) {
                const element: ExtractedElement = {
                  dbId: result.dbId,
                  name: '',
                  category: categoryProp.displayValue,
                  properties: {}
                };

                // Extract all properties
                result.properties.forEach(prop => {
                  const value = prop.displayValue;
                  
                  switch(prop.displayName) {
                    case 'Name':
                      element.name = value || 'Unnamed';
                      break;
                    case 'Family':
                      element.family = value;
                      break;
                    case 'Type':
                      element.type = value;
                      break;
                    case 'Level':
                      element.level = value;
                      break;
                    default:
                      element.properties[prop.displayName] = value;
                  }
                });

                // Use tree node name if element name is empty
                if (!element.name || element.name === 'Unnamed') {
                  element.name = tree.getNodeName(result.dbId) || `Element ${result.dbId}`;
                }

                extractedElements.push(element);
              }
            });

            processedBatches++;
            if (onProgress) {
              onProgress((processedBatches / totalBatches) * 100);
            }
            resolve();
          },
          (error) => {
            console.error('Error in bulk properties:', error);
            resolve();
          }
        );
      });
    }

    return extractedElements;
  }

  /**
   * Extract elements by level range
   * Useful for floor-based analysis
   */
  static async extractByLevel(
    viewer: Autodesk.Viewing.GuiViewer3D,
    levelMin: number,
    levelMax: number,
    onProgress?: (progress: number) => void
  ): Promise<ExtractedElement[]> {
    if (!viewer || !viewer.model) {
      throw new Error('Viewer or model not available');
    }

    const tree = viewer.model.getInstanceTree();
    if (!tree) {
      throw new Error('Model tree not available');
    }

    // Get all element IDs
    const allDbIds: number[] = [];
    tree.enumNodeChildren(tree.getRootId(), (dbId) => {
      allDbIds.push(dbId);
    }, true);

    const extractedElements: ExtractedElement[] = [];
    const batchSize = 50;
    const totalBatches = Math.ceil(allDbIds.length / batchSize);
    let processedBatches = 0;

    // Process in batches
    for (let i = 0; i < allDbIds.length; i += batchSize) {
      const batch = allDbIds.slice(i, i + batchSize);

      await new Promise<void>((resolve) => {
        viewer.model.getBulkProperties(batch, 
          ['Category', 'Name', 'Family', 'Type', 'Level', 'Element ID'],
          (results) => {
            results.forEach(result => {
              // Check if element is within level range
              const box = viewer.impl.getFragmentList().getWorldBounds(result.dbId);
              if (box) {
                const elementY = (box.min.y + box.max.y) / 2;
                
                if (elementY >= levelMin && elementY <= levelMax) {
                  const element: ExtractedElement = {
                    dbId: result.dbId,
                    name: '',
                    category: '',
                    properties: {}
                  };

                  // Extract all properties
                  result.properties.forEach(prop => {
                    const value = prop.displayValue;
                    
                    switch(prop.displayName) {
                      case 'Name':
                        element.name = value || 'Unnamed';
                        break;
                      case 'Category':
                        element.category = value || 'Unknown';
                        break;
                      case 'Family':
                        element.family = value;
                        break;
                      case 'Type':
                        element.type = value;
                        break;
                      case 'Level':
                        element.level = value;
                        break;
                      default:
                        element.properties[prop.displayName] = value;
                    }
                  });

                  // Use tree node name if element name is empty
                  if (!element.name || element.name === 'Unnamed') {
                    element.name = tree.getNodeName(result.dbId) || `Element ${result.dbId}`;
                  }

                  extractedElements.push(element);
                }
              }
            });

            processedBatches++;
            if (onProgress) {
              onProgress((processedBatches / totalBatches) * 100);
            }
            resolve();
          },
          (error) => {
            console.error('Error in bulk properties:', error);
            resolve();
          }
        );
      });
    }

    return extractedElements;
  }

  /**
   * Export elements to JSON
   */
  static exportToJSON(elements: ExtractedElement[], category: string) {
    const exportData = {
      timestamp: new Date().toISOString(),
      category: category,
      totalElements: elements.length,
      elements: elements.map(el => ({
        dbId: el.dbId,
        name: el.name,
        family: el.family,
        type: el.type,
        level: el.level,
        properties: el.properties
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
      type: 'application/json' 
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extraction_${category.replace(/\s+/g, '_')}_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}